import { useMemo, useState } from "react";

import type {
  EvaluationAnalyticsRow,
  FactoryAnalyticsSummary,
  GroundTruthAnalytics,
  PortfolioAnalytics,
  PortfolioProjectAnalytics,
  ProjectAnalytics,
  TimingAnalyticsRow,
} from "@/types/factory";
import {
  groupFactoryTimingRows,
  buildFactoryTimingCsv,
  timingWorklistLabel,
} from "@/utils/factoryTimingCsv";
import { downloadCsv } from "@/utils/uploadsCsv";
import { StatusBadge, type StatusTone } from "@/components/labeling/overview/primitives";

type SortDirection = "asc" | "desc";

function compareValues(left: unknown, right: unknown): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (typeof left === "boolean" && typeof right === "boolean") return Number(left) - Number(right);
  return String(left).localeCompare(String(right), undefined, { numeric: true });
}

function sortedRows<T>(rows: T[], key: keyof T, direction: SortDirection): T[] {
  const multiplier = direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => compareValues(left[key], right[key]) * multiplier);
}

interface SortableHeadProps {
  label: string;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: "left" | "right";
}

function SortableHead({ label, active, direction, onClick, align = "left" }: SortableHeadProps) {
  return (
    <th
      className={`px-4 py-2.5 font-medium ${align === "right" ? "text-right" : "text-left"}`}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 transition hover:text-fg"
      >
        {label}
        <span className={active ? "text-primary" : "text-fg-faint"}>
          {active ? (direction === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainder}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function formatPercent(value: number | null | undefined): string {
  return value == null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function timingCoverage(summary: FactoryAnalyticsSummary): number | null {
  if (summary.timing_coverage != null) return summary.timing_coverage;
  return summary.completed_cases > 0 ? summary.timed_cases / summary.completed_cases : null;
}

function MetricTile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="border-r border-border/50 px-5 py-4 last:border-r-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.13em] text-fg-dim">{label}</div>
      <div className="mt-1.5 font-mono text-2xl font-semibold text-fg">{value}</div>
      <div className="mt-1 text-[11px] text-fg-faint">{detail}</div>
    </div>
  );
}

function SummaryStrip({ summary }: { summary: FactoryAnalyticsSummary }) {
  return (
    <div className="grid grid-cols-2 border-b border-border/50 md:grid-cols-5">
      <MetricTile
        label="Completed cases"
        value={summary.completed_cases.toLocaleString()}
        detail="delivered assignments"
      />
      <MetricTile
        label="Unique studies"
        value={summary.unique_completed_cases.toLocaleString()}
        detail="overlap deduplicated"
      />
      <MetricTile
        label="Stones"
        value={summary.stone_count.toLocaleString()}
        detail="primary stone masks"
      />
      <MetricTile
        label="Timing coverage"
        value={formatPercent(timingCoverage(summary))}
        detail={`${summary.timed_cases.toLocaleString()} completed cases timed`}
      />
      <MetricTile
        label="Mean evaluation"
        value={formatPercent(summary.mean_evaluation_score)}
        detail={`${summary.evaluated_batches.toLocaleString()} scored batches`}
      />
    </div>
  );
}

function GroundTruthCard({ groundTruth }: { groundTruth: GroundTruthAnalytics }) {
  const incomplete = groundTruth.exists && groundTruth.annotated_cases < groundTruth.case_count;
  return (
    <div className="border-b border-border/50 bg-surface-2/30 px-5 py-3.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <StatusBadge tone={groundTruth.exists ? "success" : "warn"}>Ground truth</StatusBadge>
        <span className="font-mono text-xs text-fg">{groundTruth.worklist_project_id}</span>
        <span className="text-xs text-fg-muted">
          {groundTruth.annotated_cases} / {groundTruth.case_count} cases annotated
        </span>
        <span className="text-xs text-fg-muted">{groundTruth.stone_count} stones</span>
        <span className="text-xs text-fg-muted">
          {groundTruth.completed_cases} completion markers
        </span>
        {incomplete && (
          <span className="ml-auto text-[11px] text-warn">
            Evaluation coverage is limited by unfinished GT masks
          </span>
        )}
        {!groundTruth.exists && (
          <span className="ml-auto text-[11px] text-warn">Reference worklist not found</span>
        )}
      </div>
    </div>
  );
}

function TimingWorklist({ row }: { row: TimingAnalyticsRow }) {
  return (
    <div
      role="group"
      aria-label={`${row.worklist_project_id} timing for ${row.annotator}`}
      className="space-y-1 border-b border-border/50 pb-3 last:border-0 last:pb-0"
    >
      <div className="break-all font-mono text-[11px] text-fg">{row.worklist_project_id}</div>
      <div className="text-[10px] text-fg-muted">{timingWorklistLabel(row)}</div>
      <div className="font-mono text-sm font-semibold text-fg">
        {formatDuration(row.average_case_time_seconds)}{" "}
        <span className="text-[10px] font-normal text-fg-faint">avg completed case</span>
      </div>
      <div className="text-[11px] text-fg-muted">
        {row.timed_cases}/{row.completed_cases} timed · {formatPercent(row.timing_coverage)}
      </div>
      <div className="text-[10px] text-fg-faint">
        {row.stone_count} stones · {formatDuration(row.average_annotation_seconds)} avg annotation
      </div>
      <div className="text-[10px] text-fg-faint">Last active: {formatDate(row.last_activity)}</div>
    </div>
  );
}

function TimingCell({ worklists }: { worklists: TimingAnalyticsRow[] | undefined }) {
  if (!worklists) return <span className="text-fg-faint">—</span>;
  return (
    <div className="space-y-3">
      {worklists.map((row) => (
        <TimingWorklist key={row.id} row={row} />
      ))}
    </div>
  );
}

function TimingTable({
  rows,
  projects,
}: {
  rows: TimingAnalyticsRow[];
  projects: Array<{ project_id: string; project_name: string }>;
}) {
  const matrix = useMemo(() => groupFactoryTimingRows(rows), [rows]);

  if (rows.length === 0) {
    return <div className="p-8 text-center text-sm text-fg-muted">No timing data yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-xs">
        <thead>
          <tr className="border-b border-border/50 uppercase tracking-wide text-fg-dim">
            <th className="sticky left-0 bg-surface px-4 py-2.5 font-medium">Annotator</th>
            {projects.map((project) => (
              <th key={project.project_id} className="min-w-[190px] px-4 py-2.5 font-medium">
                <div className="normal-case tracking-normal text-fg-muted">
                  {project.project_name}
                </div>
                <div className="font-mono text-[9px] font-normal normal-case tracking-normal text-fg-faint">
                  {project.project_id}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.annotator} className="border-b border-border/50 last:border-0">
              <td className="sticky left-0 bg-surface px-4 py-3 align-top font-mono text-[11px] text-fg">
                {row.annotator}
              </td>
              {projects.map((project) => (
                <td key={project.project_id} className="px-4 py-3 align-top">
                  <TimingCell worklists={row.projects.get(project.project_id)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function evaluationTone(status: string, score: number | null): StatusTone {
  if (status === "not_scored" || status === "not_applicable") return "ghost";
  if (status !== "ok") return "warn";
  return score != null && score >= 0.8 ? "success" : "warn";
}

function evaluationResultSummary(result: Record<string, unknown>): string {
  const status = typeof result.status === "string" ? result.status : "complete";
  const comparisonScore =
    typeof result.comparison_score === "number" ? result.comparison_score : null;
  const mask =
    result.mask && typeof result.mask === "object"
      ? (result.mask as Record<string, unknown>)
      : null;
  const meanDice = typeof mask?.mean_dice === "number" ? mask.mean_dice : null;
  const score = comparisonScore ?? meanDice;
  if (status === "ok") {
    return score == null ? "Evaluation complete" : `Evaluation complete · ${formatPercent(score)}`;
  }
  return status.replaceAll("_", " ");
}

function evaluationErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (error instanceof Error && error.message) return error.message;
  return "Evaluation failed";
}

function EvaluationTable({
  rows,
  showProject,
  onRunEvaluation,
}: {
  rows: EvaluationAnalyticsRow[];
  showProject: boolean;
  onRunEvaluation?: (row: EvaluationAnalyticsRow) => Promise<Record<string, unknown>>;
}) {
  const [sortKey, setSortKey] = useState<keyof EvaluationAnalyticsRow>("computed_at");
  const [direction, setDirection] = useState<SortDirection>("desc");
  const sorted = useMemo(() => sortedRows(rows, sortKey, direction), [rows, sortKey, direction]);
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<
    Record<string, { tone: "success" | "error"; message: string }>
  >({});
  const sort = (key: keyof EvaluationAnalyticsRow) => {
    if (sortKey === key) setDirection((value) => (value === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDirection("asc");
    }
  };
  const head = (label: string, key: keyof EvaluationAnalyticsRow, align?: "left" | "right") => (
    <SortableHead
      label={label}
      active={sortKey === key}
      direction={direction}
      onClick={() => sort(key)}
      align={align}
    />
  );
  const runEvaluation = async (row: EvaluationAnalyticsRow) => {
    if (!onRunEvaluation) return;
    const key = `${row.project_id}-${row.id}`;
    setRunningKey(key);
    setOutcomes((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    try {
      const result = await onRunEvaluation(row);
      setOutcomes((current) => ({
        ...current,
        [key]: { tone: "success", message: evaluationResultSummary(result) },
      }));
    } catch (error) {
      setOutcomes((current) => ({
        ...current,
        [key]: { tone: "error", message: evaluationErrorMessage(error) },
      }));
    } finally {
      setRunningKey(null);
    }
  };

  if (rows.length === 0) {
    return <div className="p-8 text-center text-sm text-fg-muted">No evaluation batches yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1280px] text-left text-xs">
        <thead>
          <tr className="border-b border-border/50 uppercase tracking-wide text-fg-dim">
            {showProject && head("Project", "project_name")}
            {head("Batch / worklist", "worklist_project_id")}
            {head("Annotator", "annotator")}
            {head("Phase", "phase")}
            {head("Status", "status")}
            {head("Metric", "metric")}
            {head("Score", "score", "right")}
            {head("Dice / IoU", "mean_dice", "right")}
            {head("Coverage", "n_scored", "right")}
            {head("Evaluated", "computed_at")}
            {onRunEvaluation && <th className="px-4 py-2.5 font-medium">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const key = `${row.project_id}-${row.id}`;
            const running = runningKey === key;
            const outcome = outcomes[key];
            return (
              <tr key={key} className="border-b border-border/50 last:border-0">
                {showProject && (
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-fg">{row.project_name}</div>
                    <div className="font-mono text-[10px] text-fg-faint">{row.project_id}</div>
                  </td>
                )}
                <td className="max-w-[300px] px-4 py-3 align-top">
                  <div className="break-all font-mono text-[11px] text-fg">
                    {row.worklist_project_id}
                  </div>
                  <div className="mt-1 break-all text-[10px] text-fg-faint">{row.batch_id}</div>
                </td>
                <td className="px-4 py-3 align-top font-mono text-[11px] text-fg-muted">
                  {row.annotator}
                </td>
                <td className="px-4 py-3 align-top text-fg-muted">{row.phase}</td>
                <td className="px-4 py-3 align-top">
                  <StatusBadge tone={evaluationTone(row.status, row.score)}>
                    {row.status.replaceAll("_", " ")}
                  </StatusBadge>
                </td>
                <td className="px-4 py-3 align-top text-fg-muted">
                  {row.mode ?? "—"} · {row.metric?.replaceAll("_", " ") ?? "—"}
                </td>
                <td className="px-4 py-3 text-right align-top font-mono font-semibold text-fg">
                  {formatPercent(row.score)}
                </td>
                <td className="px-4 py-3 text-right align-top font-mono text-fg-muted">
                  {formatPercent(row.mean_dice)} / {formatPercent(row.mean_iou)}
                </td>
                <td className="px-4 py-3 text-right align-top font-mono text-fg-muted">
                  <div>{row.n_scored} scored</div>
                  <div className="text-[10px] text-fg-faint">{row.missing_cases} missing</div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 align-top text-fg-muted">
                  {formatDate(row.computed_at)}
                </td>
                {onRunEvaluation && (
                  <td className="min-w-[180px] px-4 py-3 align-top">
                    <button
                      type="button"
                      disabled={runningKey !== null}
                      onClick={() => void runEvaluation(row)}
                      className="rounded border border-border-strong px-2.5 py-1.5 text-[11px] font-medium text-fg transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Recompute this batch without changing its saved scorecard or workflow state"
                    >
                      {running ? "Running…" : outcome ? "Run again" : "Run evaluation"}
                    </button>
                    {outcome && (
                      <div
                        role={outcome.tone === "error" ? "alert" : "status"}
                        className={`mt-1.5 text-[10px] ${outcome.tone === "error" ? "text-warn" : "text-fg-muted"}`}
                      >
                        {outcome.message}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface RollupRow extends PortfolioProjectAnalytics {
  timing_coverage: number | null;
  completed_cases: number;
  unique_completed_cases: number;
  stone_count: number;
  evaluation_score: number | null;
  evaluated_batches: number;
}

function ProjectRollupTable({ projects }: { projects: PortfolioProjectAnalytics[] }) {
  const rows = useMemo<RollupRow[]>(
    () =>
      projects.map((project) => ({
        ...project,
        timing_coverage: timingCoverage(project.summary),
        completed_cases: project.summary.completed_cases,
        unique_completed_cases: project.summary.unique_completed_cases,
        stone_count: project.summary.stone_count,
        evaluation_score: project.summary.mean_evaluation_score,
        evaluated_batches: project.summary.evaluated_batches,
      })),
    [projects],
  );
  const [sortKey, setSortKey] = useState<keyof RollupRow>("project_name");
  const [direction, setDirection] = useState<SortDirection>("asc");
  const sorted = useMemo(() => {
    if (sortKey === "summary" || sortKey === "ground_truth") return rows;
    return sortedRows(rows, sortKey, direction);
  }, [rows, sortKey, direction]);
  const sort = (key: keyof RollupRow) => {
    if (sortKey === key) setDirection((value) => (value === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDirection("asc");
    }
  };

  return (
    <div className="border-b border-border/50">
      <div className="border-b border-border/50 px-5 py-3 text-[11px] font-medium uppercase tracking-wide text-fg-dim">
        Project rollup
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead>
            <tr className="border-b border-border/50 uppercase tracking-wide text-fg-dim">
              <SortableHead
                label="Project"
                active={sortKey === "project_name"}
                direction={direction}
                onClick={() => sort("project_name")}
              />
              <SortableHead
                label="Completed"
                active={sortKey === "completed_cases"}
                direction={direction}
                onClick={() => sort("completed_cases")}
                align="right"
              />
              <SortableHead
                label="Unique"
                active={sortKey === "unique_completed_cases"}
                direction={direction}
                onClick={() => sort("unique_completed_cases")}
                align="right"
              />
              <SortableHead
                label="Stones"
                active={sortKey === "stone_count"}
                direction={direction}
                onClick={() => sort("stone_count")}
                align="right"
              />
              <SortableHead
                label="Timing coverage"
                active={sortKey === "timing_coverage"}
                direction={direction}
                onClick={() => sort("timing_coverage")}
                align="right"
              />
              <SortableHead
                label="Evaluation"
                active={sortKey === "evaluation_score"}
                direction={direction}
                onClick={() => sort("evaluation_score")}
                align="right"
              />
              <th className="px-4 py-2.5 font-medium">Ground truth</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((project) => (
              <tr key={project.project_id} className="border-b border-border/50 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-fg">{project.project_name}</div>
                  <div className="font-mono text-[10px] text-fg-faint">
                    {project.project_id} · {project.task}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-fg">
                  {project.summary.completed_cases}
                </td>
                <td className="px-4 py-3 text-right font-mono text-fg-muted">
                  {project.summary.unique_completed_cases}
                </td>
                <td className="px-4 py-3 text-right font-mono text-fg">
                  {project.summary.stone_count}
                </td>
                <td className="px-4 py-3 text-right font-mono text-fg-muted">
                  {formatPercent(project.timing_coverage)}
                </td>
                <td className="px-4 py-3 text-right font-mono text-fg-muted">
                  <div>{formatPercent(project.summary.mean_evaluation_score)}</div>
                  <div className="text-[10px] text-fg-faint">
                    {project.summary.evaluated_batches} batches
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-[11px] text-fg-muted">
                    {project.ground_truth.worklist_project_id}
                  </div>
                  <div className="text-[10px] text-fg-faint">
                    {project.ground_truth.annotated_cases}/{project.ground_truth.case_count}{" "}
                    annotated
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export interface AnalyticsPanelProps {
  analytics: ProjectAnalytics | PortfolioAnalytics;
  scope: "project" | "portfolio";
  timingWindow?: { startDate: string; endDate: string };
  onTimingWindowChange?: (window: { startDate: string; endDate: string }) => void;
  onRunEvaluation?: (row: EvaluationAnalyticsRow) => Promise<Record<string, unknown>>;
}

export function AnalyticsPanel({
  analytics,
  scope,
  timingWindow,
  onTimingWindowChange,
  onRunEvaluation,
}: AnalyticsPanelProps) {
  const showProject = scope === "portfolio";
  const [startDate, setStartDate] = useState(timingWindow?.startDate ?? "");
  const [endDate, setEndDate] = useState(timingWindow?.endDate ?? "");
  const timingProjects = useMemo(() => {
    if (scope === "portfolio" && "projects" in analytics) {
      return analytics.projects.map(({ project_id, project_name }) => ({
        project_id,
        project_name,
      }));
    }
    if ("project_id" in analytics) {
      return [{ project_id: analytics.project_id, project_name: analytics.project_name }];
    }
    return [];
  }, [analytics, scope]);
  const invalidWindow = Boolean(startDate && endDate && startDate > endDate);
  const exportTiming = () => {
    const csv = buildFactoryTimingCsv(
      analytics.timing_rows,
      timingWindow?.startDate ?? startDate,
      timingWindow?.endDate ?? endDate,
    );
    const range = [timingWindow?.startDate ?? startDate, timingWindow?.endDate ?? endDate]
      .filter(Boolean)
      .join("-to-");
    const exportScope =
      scope === "project" && "project_id" in analytics ? analytics.project_id : "all-projects";
    const safeScope = exportScope.replace(/[^a-zA-Z0-9._-]+/g, "-");
    downloadCsv(`segmentation-timing-${safeScope}${range ? `-${range}` : ""}.csv`, csv);
  };
  return (
    <section id="analytics" className="rounded border border-border/50 bg-surface">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/50 px-5 py-3.5">
        <div>
          <h2 className="text-sm font-semibold text-fg">
            {showProject ? "All-project production analytics" : "Production analytics"}
          </h2>
          <p className="mt-0.5 text-[11px] text-fg-faint">
            {showProject
              ? "Completion and timing across segmentation projects"
              : "Completion, timing, and persisted segmentation evaluation details"}
          </p>
        </div>
        <div className="ml-auto font-mono text-[11px] text-fg-faint">
          {scope === "project" && `${analytics.evaluation_rows.length} evaluations · `}
          {analytics.timing_rows.length} timing records
        </div>
      </div>

      <SummaryStrip summary={analytics.summary} />
      {scope === "project" && "ground_truth" in analytics && (
        <GroundTruthCard groundTruth={analytics.ground_truth} />
      )}
      {scope === "portfolio" && "projects" in analytics && (
        <ProjectRollupTable projects={analytics.projects} />
      )}

      {scope === "project" && (
        <>
          <div className="border-b border-border/50 px-5 py-3 text-[11px] font-medium uppercase tracking-wide text-fg-dim">
            Evaluation detail
          </div>
          <EvaluationTable
            rows={analytics.evaluation_rows}
            showProject={false}
            onRunEvaluation={onRunEvaluation}
          />
        </>
      )}

      <div className="flex flex-wrap items-end gap-3 border-y border-border/50 px-5 py-3">
        <div className="mr-auto text-[11px] font-medium uppercase tracking-wide text-fg-dim">
          Worklist timing · grouped by annotator
        </div>
        {timingWindow && onTimingWindowChange && (
          <>
            <label className="text-[10px] uppercase tracking-wide text-fg-dim">
              Latest activity from
              <input
                aria-label="Latest activity from"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(event) => setStartDate(event.target.value)}
                className="mt-1 block rounded border border-border-strong bg-surface-2 px-2.5 py-1.5 text-xs normal-case tracking-normal text-fg"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-fg-dim">
              Latest activity through
              <input
                aria-label="Latest activity through"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(event) => setEndDate(event.target.value)}
                className="mt-1 block rounded border border-border-strong bg-surface-2 px-2.5 py-1.5 text-xs normal-case tracking-normal text-fg"
              />
            </label>
            <button
              type="button"
              disabled={invalidWindow}
              onClick={() => onTimingWindowChange({ startDate, endDate })}
              className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Load
            </button>
            <button
              type="button"
              disabled={analytics.timing_rows.length === 0}
              onClick={exportTiming}
              className="rounded border border-border-strong px-3 py-1.5 text-xs font-medium text-fg transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Export CSV
            </button>
          </>
        )}
      </div>
      <div className="space-y-1 border-b border-border/50 px-5 py-3 text-[11px] text-fg-muted">
        <p>
          Worklist detail includes training, development, and reference activity, which are excluded
          from production totals above.
        </p>
        <p>
          Dates select latest activity. Timers are cumulative, not time worked during the selected
          period. Averages use timed completed cases and annotations with recorded duration.
        </p>
        <p>Reference inventory and saved evaluations are not filtered by these dates.</p>
      </div>
      <TimingTable rows={analytics.timing_rows} projects={timingProjects} />
    </section>
  );
}

export default AnalyticsPanel;
